/**
 * Biometric safe-unlock via the WebAuthn PRF extension.
 *
 * A web page cannot reach the OS biometric through a native companion the way
 * the browser extensions do (Native Messaging is extension-only). Instead we use
 * the in-browser WebAuthn **PRF** extension: a platform-authenticator credential
 * (Touch ID / Face ID / Windows Hello / Android fingerprint) yields a stable
 * per-credential secret, which we HKDF into an AES-256-GCM key and use to wrap the
 * user's **safe password**. Unlock re-derives the same key (behind a live
 * biometric prompt), decrypts the password, and feeds it to the existing
 * `SafeContext.unlock()` — no new crypto path downstream, zero-knowledge intact
 * (only ciphertext is persisted; nothing leaves the device).
 *
 * PRF support is authenticator-dependent, so this is a progressive enhancement:
 * enrollment only completes when a real PRF result comes back, and the password
 * field is always available as a fallback. See
 * docs/superpowers/specs/2026-07-17-web-app-biometric-unlock-design.md.
 */
import {
  EXTENSION_PRESENCE_ATTR,
  HBS_WEB_MESSAGE_SOURCE,
  HBS_EXT_MESSAGE_SOURCE,
  HBS_SUSPEND_PASSKEY_PROVIDER,
  HBS_PASSKEY_PROVIDER_SUSPENDED,
  HBS_RESUME_PASSKEY_PROVIDER,
  HBS_PASSKEY_PROVIDER_RESUMED,
} from '@heybesafe/shared';
import { base64ToBytes, bytesToBase64 } from './crypto';

const RP_NAME = 'Hey Be Safe';
/** HKDF `info` label binding the derived key to this feature. */
const HKDF_INFO = new TextEncoder().encode('hbs-web-biometric-unlock');

/** The persisted enrollment: everything needed to re-derive the key and decrypt. */
export interface BiometricEnrollment {
  /** base64 raw credential id. */
  credentialId: string;
  /** base64 32-byte PRF eval salt (per-enrollment random). */
  prfSalt: string;
  /** base64 AES-GCM ciphertext of the safe password. */
  wrappedPassword: string;
  /** base64 AES-GCM iv. */
  wrappedPasswordIv: string;
}

export type BiometricErrorCode =
  | 'UNSUPPORTED'   // no WebAuthn / no platform authenticator
  | 'NO_PRF'        // authenticator does not support the PRF extension
  | 'USER_CANCELED' // user dismissed the biometric prompt
  | 'FAILED';       // anything else

export class BiometricUnlockError extends Error {
  constructor(public readonly code: BiometricErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'BiometricUnlockError';
  }
}

/** Shape of `getClientExtensionResults().prf` (not in the DOM lib types yet). */
interface PrfExtensionResults {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
}

function rpId(): string {
  return window.location.hostname;
}

// Typed as Uint8Array<ArrayBuffer> so the bytes satisfy `BufferSource` for the
// WebAuthn / WebCrypto call sites under the project's strict DOM lib (matches the
// cast style in crypto.ts).
function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(length) as Uint8Array<ArrayBuffer>);
}

function mapCeremonyError(err: unknown): BiometricUnlockError {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError') return new BiometricUnlockError('USER_CANCELED');
  return new BiometricUnlockError('FAILED', String(err));
}

// ─── Extension passkey-provider detach handshake ─────────────────────────────
//
// If the Hey Be Safe browser extension is installed with its passkey provider
// enabled, its `chrome.webAuthenticationProxy` provider intercepts EVERY
// WebAuthn call profile-wide and returns no PRF — shadowing the native platform
// authenticator on heybesafe.com, so our create()/get() PRF ceremony can't
// complete. The extension can't preserve native PRF for our site while the
// provider is attached (the API is all-or-nothing), so instead the web app asks
// it to briefly detach the provider around the ceremony and re-attach after.
//
// The extension's content script (own-origin only) relays these window messages
// to its service worker. If no extension is installed, nothing answers and
// `requestProviderSuspend` resolves `false` after a short timeout — the ceremony
// then runs unmodified (there's no provider to shadow it). Contract lives in
// @heybesafe/shared (passkeyDetachHandshake.ts).

/** Whether this environment can do `window.postMessage`-based messaging. False
 *  in SSR / non-browser test stubs — the handshake is then skipped and the
 *  ceremony runs unmodified. */
function hasWindowMessaging(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.addEventListener === 'function' &&
    typeof window.postMessage === 'function'
  );
}

// Ack timeout when the extension is NOT detected on the page — there's likely
// no relay to answer, so we only briefly wait before running the ceremony
// unmodified. Keeps the common (no-extension) unlock instant.
const ACK_TIMEOUT_NO_EXT_MS = 250;
// Ack timeout when the extension IS detected. A dormant MV3 service worker must
// wake, evaluate its module (which kicks off the OPAQUE WASM load), and register
// its onMessage listener before the relay can reach it; the relay retries the
// wake race across a ~2.5s budget (WAKE_MAX_RETRIES × WAKE_RETRY_DELAY_MS in
// passkey-detach-relay.ts). We must wait past that budget so the page is still
// listening when the relay lands the SW's TRUE reply — otherwise a timeout here
// makes us run the ceremony with the provider still attached, and the native
// authenticator returns no PRF ("isn't available in this browser"). Only a cold
// SW ever approaches this ceiling; a warm one replies in a few ms.
const ACK_TIMEOUT_EXT_MS = 3500;

/** True when the extension's content script has tagged this page (see
 *  `EXTENSION_PRESENCE_ATTR`). Governs how long we wait for the relay's ack. */
function isExtensionPresent(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.documentElement?.hasAttribute(EXTENSION_PRESENCE_ATTR) === true
  );
}

/**
 * Post `outType` to the extension relay and resolve when its `replyType` ack
 * comes back (via `extract`), or `fallback` on timeout. Shared skeleton for the
 * suspend/resume handshake — same-origin, same-window validation, always
 * cleans up its listener + timer.
 */
function awaitRelayAck<T>(
  outType: typeof HBS_SUSPEND_PASSKEY_PROVIDER | typeof HBS_RESUME_PASSKEY_PROVIDER,
  replyType: typeof HBS_PASSKEY_PROVIDER_SUSPENDED | typeof HBS_PASSKEY_PROVIDER_RESUMED,
  extract: (data: { wasAttached?: unknown }) => T,
  fallback: T,
): Promise<T> {
  if (!hasWindowMessaging()) return Promise.resolve(fallback);
  const timeoutMs = isExtensionPresent() ? ACK_TIMEOUT_EXT_MS : ACK_TIMEOUT_NO_EXT_MS;
  return new Promise<T>((resolve) => {
    let done = false;
    const finish = (result: T): void => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(result);
    };
    const onMessage = (event: MessageEvent): void => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const data = event.data as { source?: unknown; type?: unknown; wasAttached?: unknown } | null;
      if (!data || data.source !== HBS_EXT_MESSAGE_SOURCE || data.type !== replyType) return;
      finish(extract(data));
    };
    const timer = setTimeout(() => finish(fallback), timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ source: HBS_WEB_MESSAGE_SOURCE, type: outType }, window.location.origin);
  });
}

/**
 * Ask the extension to detach its passkey provider. Resolves `true` only if the
 * extension acknowledged an actual detach (provider was attached); `false` if no
 * extension answered within the timeout or the provider wasn't attached. Only on
 * `true` should the caller later ask to resume.
 */
function requestProviderSuspend(): Promise<boolean> {
  return awaitRelayAck(
    HBS_SUSPEND_PASSKEY_PROVIDER,
    HBS_PASSKEY_PROVIDER_SUSPENDED,
    (data) => data.wasAttached === true,
    false,
  );
}

/** Ask the extension to re-attach its passkey provider after the ceremony.
 *  Best-effort: resolves on the RESUMED ack or a short timeout (the extension's
 *  own watchdog is the backstop). */
function requestProviderResume(): Promise<void> {
  return awaitRelayAck(
    HBS_RESUME_PASSKEY_PROVIDER,
    HBS_PASSKEY_PROVIDER_RESUMED,
    () => undefined,
    undefined,
  );
}

/**
 * Run `fn` with the extension's passkey provider detached (if any), then always
 * resume — even if `fn` throws. A no-op wrapper when no extension is present.
 */
async function withProviderDetached<T>(fn: () => Promise<T>): Promise<T> {
  const suspended = await requestProviderSuspend();
  try {
    return await fn();
  } finally {
    if (suspended) await requestProviderResume();
  }
}

/**
 * Coarse platform gate. `true` does NOT guarantee PRF — that's only known once a
 * real ceremony returns a PRF result (see `enrollBiometric`).
 */
export async function isBiometricUnlockSupported(): Promise<boolean> {
  try {
    if (typeof window === 'undefined' || typeof window.PublicKeyCredential === 'undefined') {
      return false;
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Prompt the biometric, evaluate the PRF over `prfSalt`, and HKDF the result into
 * an AES-256-GCM wrapping key. This is where the OS biometric dialog appears.
 */
async function deriveWrapKey(
  credentialId: Uint8Array<ArrayBuffer>,
  prfSalt: Uint8Array<ArrayBuffer>,
): Promise<CryptoKey> {
  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: {
        rpId: rpId(),
        challenge: randomBytes(32),
        allowCredentials: [{ type: 'public-key', id: credentialId, transports: ['internal'] }],
        userVerification: 'required',
        timeout: 60_000,
        extensions: { prf: { eval: { first: prfSalt } } },
      },
    })) as PublicKeyCredential | null;
  } catch (err) {
    throw mapCeremonyError(err);
  }
  if (!assertion) throw new BiometricUnlockError('FAILED', 'No assertion returned');

  const ext = assertion.getClientExtensionResults() as PrfExtensionResults;
  const prf = ext?.prf?.results?.first;
  if (!prf) throw new BiometricUnlockError('NO_PRF', 'Authenticator returned no PRF result');

  const keyMaterial = await crypto.subtle.importKey('raw', new Uint8Array(prf), 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32) as Uint8Array<ArrayBuffer>, info: HKDF_INFO },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Enroll biometric unlock: create a platform PRF credential on this origin, derive
 * the wrapping key, and encrypt `safePassword` under it. Returns the blob to
 * persist. Throws `BiometricUnlockError` (`NO_PRF` if the authenticator can't do
 * PRF, `USER_CANCELED`, etc.) — the caller must not persist on error.
 *
 * Two ceremonies here (create, then get) because some browsers only surface the
 * PRF output from an assertion, not from creation. Unlock is a single ceremony.
 */
export async function enrollBiometric(safePassword: string): Promise<BiometricEnrollment> {
  if (!(await isBiometricUnlockSupported())) throw new BiometricUnlockError('UNSUPPORTED');

  const prfSalt = randomBytes(32);
  // Both ceremonies (create, then get inside deriveWrapKey) run inside ONE
  // provider-detach window so an installed extension's passkey provider doesn't
  // shadow the native authenticator, and so we don't flap detach→attach→detach
  // between the two ceremonies.
  const { credentialId, wrapKey } = await withProviderDetached(async () => {
    let credential: PublicKeyCredential | null;
    try {
      credential = (await navigator.credentials.create({
        publicKey: {
          rp: { id: rpId(), name: RP_NAME },
          user: { id: randomBytes(16), name: 'safe-unlock', displayName: RP_NAME },
          challenge: randomBytes(32),
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 },   // ES256
            { type: 'public-key', alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            residentKey: 'required',
            userVerification: 'required',
          },
          timeout: 60_000,
          extensions: { prf: {} },
        },
      })) as PublicKeyCredential | null;
    } catch (err) {
      throw mapCeremonyError(err);
    }
    if (!credential) throw new BiometricUnlockError('FAILED', 'No credential created');

    // Gate on `getClientExtensionResults().prf.enabled` ONLY when the authenticator
    // explicitly reports `false` — then PRF definitely won't work, so bail now
    // instead of running a second biometric prompt (the `get()` in deriveWrapKey)
    // that can only fail. This is the common Chrome-on-macOS case: a platform
    // passkey routed to iCloud Keychain reports `enabled: false` before macOS 15
    // (Sequoia), where iCloud Keychain gained PRF.
    //
    // We must NOT gate on a *missing* `enabled`: several browsers that fully
    // support PRF don't populate it on *creation* and only surface PRF from the
    // `get()` assertion (Safari, some Chrome/authenticator combos). For those the
    // authoritative signal is a real PRF result, so we still go to `deriveWrapKey`,
    // which throws `NO_PRF` only if no PRF output actually comes back.
    const createPrf = (credential.getClientExtensionResults() as PrfExtensionResults).prf;
    if (createPrf?.enabled === false) {
      throw new BiometricUnlockError('NO_PRF', 'Authenticator reported PRF not enabled on creation');
    }
    const id = new Uint8Array(credential.rawId);
    const key = await deriveWrapKey(id, prfSalt);
    return { credentialId: id, wrapKey: key };
  });

  const iv = randomBytes(12);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrapKey,
    new TextEncoder().encode(safePassword),
  );

  return {
    credentialId: bytesToBase64(credentialId),
    prfSalt: bytesToBase64(prfSalt),
    wrappedPassword: bytesToBase64(new Uint8Array(ciphertext)),
    wrappedPasswordIv: bytesToBase64(iv),
  };
}

/**
 * Unlock path: prompt the biometric, re-derive the wrapping key, and decrypt the
 * stored safe password. Throws `BiometricUnlockError` on cancel/failure.
 */
export async function unwrapPasswordWithBiometric(enrollment: BiometricEnrollment): Promise<string> {
  // Detach an installed extension's passkey provider around the get() so the
  // biometric prompt reaches the native authenticator (see withProviderDetached).
  const wrapKey = await withProviderDetached(() =>
    deriveWrapKey(base64ToBytes(enrollment.credentialId), base64ToBytes(enrollment.prfSalt)),
  );
  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(enrollment.wrappedPasswordIv) },
      wrapKey,
      base64ToBytes(enrollment.wrappedPassword),
    );
  } catch {
    // Tag mismatch — the credential no longer maps to the same key (rotated /
    // reset enrollment). Treat as a failed unlock; the caller clears the binding.
    throw new BiometricUnlockError('FAILED', 'Could not decrypt the stored password');
  }
  return new TextDecoder().decode(plaintext);
}
